class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    // Listen for variant changes on the main product page
    this.#listenForVariantChanges();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          // Filter out unavailable products after loading
          this.#filterUnavailableProducts();
          // Match variants with main product's selected variant
          this.#matchVariants();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Get the currently selected variant from the main product page
   * @returns {Object|null} The selected variant or null
   */
  #getMainProductVariant() {
    // Try to get variant from URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    // Try to get from product form (most reliable)
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]');
      const currentVariantId = variantInput?.value || variantId;
      
      if (currentVariantId) {
        const selectedOptions = this.#getSelectedOptionsFromForm(productForm);
        return {
          id: currentVariantId,
          options: selectedOptions
        };
      }
    }
    
    // Try to get from variant picker
    const variantPicker = document.querySelector('variant-picker, [data-variant-picker]');
    if (variantPicker) {
      const selectedOptions = this.#getSelectedOptions(variantPicker);
      const selectedVariantId = variantPicker.dataset.selectedVariantId || 
                                 variantPicker.querySelector('[data-selected="true"]')?.dataset.variantId ||
                                 variantId;
      
      if (selectedOptions && selectedOptions.length > 0) {
        return {
          id: selectedVariantId,
          options: selectedOptions
        };
      }
    }
    
    return null;
  }

  /**
   * Get variant options from a variant element
   * @param {HTMLElement} variantElement
   * @returns {Array} Array of option values
   */
  #getVariantOptions(variantElement) {
    const options = [];
    const optionInputs = variantElement.closest('form, variant-picker')?.querySelectorAll('input[type="radio"]:checked, select');
    if (optionInputs) {
      optionInputs.forEach(input => {
        if (input.type === 'radio' && input.checked) {
          options.push(input.value);
        } else if (input.tagName === 'SELECT') {
          options.push(input.value);
        }
      });
    }
    return options;
  }

  /**
   * Get selected options from product form
   * @param {HTMLElement} form
   * @returns {Array} Array of option values
   */
  #getSelectedOptionsFromForm(form) {
    const options = [];
    // Get options in order (option1, option2, option3)
    for (let i = 1; i <= 3; i++) {
      const optionInput = form.querySelector(`input[name="options[${i}]"]:checked, select[name="options[${i}]"]`);
      if (optionInput) {
        if (optionInput.type === 'radio' && optionInput.checked) {
          options.push(optionInput.value);
        } else if (optionInput.tagName === 'SELECT') {
          options.push(optionInput.value);
        }
      }
    }
    return options;
  }

  /**
   * Get selected options from variant picker
   * @param {HTMLElement} variantPicker
   * @returns {Array} Array of option values
   */
  #getSelectedOptions(variantPicker) {
    const options = [];
    const optionInputs = variantPicker.querySelectorAll('input[type="radio"]:checked, select');
    optionInputs.forEach(input => {
      if (input.type === 'radio' && input.checked) {
        options.push(input.value);
      } else if (input.tagName === 'SELECT') {
        options.push(input.value);
      }
    });
    return options;
  }

  /**
   * Find matching variant in recommended product based on main product's selected variant
   * @param {Object} mainVariant - The main product's selected variant
   * @param {Array} productVariants - Array of variants for the recommended product
   * @returns {Object|null} Matching variant or null
   */
  #findMatchingVariant(mainVariant, productVariants) {
    if (!mainVariant || !productVariants || productVariants.length === 0) {
      return null;
    }

    try {
      // Parse product variants if it's a string
      const variants = typeof productVariants === 'string' ? JSON.parse(productVariants) : productVariants;
      
      // If main variant has options, try to match by options
      if (mainVariant.options && mainVariant.options.length > 0) {
        // Find exact match first
        for (const variant of variants) {
          if (variant.options && variant.options.length === mainVariant.options.length) {
            let match = true;
            for (let i = 0; i < mainVariant.options.length; i++) {
              if (variant.options[i] !== mainVariant.options[i]) {
                match = false;
                break;
              }
            }
            if (match && variant.available) {
              return variant;
            }
          }
        }
        
        // Find partial match (same first option)
        if (mainVariant.options.length > 0) {
          for (const variant of variants) {
            if (variant.options && variant.options[0] === mainVariant.options[0] && variant.available) {
              return variant;
            }
          }
        }
      }
      
      // If no match found, return first available variant
      for (const variant of variants) {
        if (variant.available) {
          return variant;
        }
      }
      
      // Return first variant if none are available
      return variants[0] || null;
    } catch (e) {
      console.error('Error parsing product variants:', e);
      return null;
    }
  }

  /**
   * Listen for variant changes on the main product page
   */
  #listenForVariantChanges() {
    // Listen for changes on variant picker
    const variantPicker = document.querySelector('variant-picker, [data-variant-picker]');
    if (variantPicker) {
      variantPicker.addEventListener('change', () => {
        this.#matchVariants();
      });
    }

    // Listen for changes on product form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      productForm.addEventListener('change', (e) => {
        if (e.target.name && (e.target.name.startsWith('options[') || e.target.name === 'id')) {
          this.#matchVariants();
        }
      });
    }

    // Listen for URL changes (when variant is selected via URL)
    let lastUrl = window.location.href;
    setInterval(() => {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        this.#matchVariants();
      }
    }, 500);
  }

  /**
   * Match variants in recommended products with main product's selected variant
   */
  #matchVariants() {
    const mainVariant = this.#getMainProductVariant();
    if (!mainVariant) {
      return;
    }

    const productCardLinks = this.querySelectorAll('.product-card__link[data-product-variants]');
    productCardLinks.forEach(link => {
      const productVariants = link.dataset.productVariants;
      if (!productVariants) {
        return;
      }

      const matchingVariant = this.#findMatchingVariant(mainVariant, productVariants);
      if (matchingVariant) {
        // Update the link URL with the matching variant
        const productUrl = new URL(link.href);
        productUrl.searchParams.set('variant', matchingVariant.id);
        link.href = productUrl.toString();
        link.dataset.variantId = matchingVariant.id;
      }
    });
  }

  /**
   * Filter out unavailable products from recommendations
   */
  #filterUnavailableProducts() {
    const productItems = this.querySelectorAll('.resource-list__item');
    productItems.forEach((item) => {
      const productCard = item.querySelector('product-card-link');
      if (productCard) {
        const productElement = productCard.closest('.resource-list__item')?.querySelector('[data-product-id]');
        if (productElement) {
          // Check if product is unavailable by looking for data-product-unavailable attribute
          // or by checking if product-card has unavailable indicator
          const isUnavailable = productCard.dataset.productUnavailable === 'true' || 
                                productCard.querySelector('[data-product-unavailable="true"]');
          
          // Also check product availability from product-card element
          if (!isUnavailable && productCard.dataset.productAvailable === 'false') {
            item.style.display = 'none';
            item.classList.add('resource-list__item--hidden-unavailable');
          } else if (isUnavailable) {
            item.style.display = 'none';
            item.classList.add('resource-list__item--hidden-unavailable');
          }
        }
      }
    });
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
